import { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, Upload, Loader2, X, Users, ChevronDown, ChevronUp, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import { API, getAuthHeader } from './api';

const defaultForm = {
  title: '', description: '', event_type: '', date: '', time: '',
  event_datetime: '', location: '', is_virtual: false, image_url: '', is_past: false
};

export function EventsManagement() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState(defaultForm);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [viewingRegsFor, setViewingRegsFor] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [expandedReg, setExpandedReg] = useState(null);

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try { const response = await axios.get(`${API}/events`); setEvents(response.data); }
    catch { /* silent */ }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      // Auto-compute event_datetime from date + time if not set via datetime-local
      if (!payload.event_datetime && payload.date) {
        payload.event_datetime = new Date(payload.date + 'T00:00:00').toISOString();
      }
      // Auto-set is_past based on event_datetime
      if (payload.event_datetime) {
        payload.is_past = new Date(payload.event_datetime) < new Date();
      }
      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, payload, { headers: getAuthHeader() });
        toast.success('Event updated');
      } else {
        await axios.post(`${API}/events`, payload, { headers: getAuthHeader() });
        toast.success('Event created & published');
      }
      setIsModalOpen(false); setEditingEvent(null); fetchEvents();
    } catch { toast.error('Failed to save event'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try { await axios.delete(`${API}/events/${id}`, { headers: getAuthHeader() }); toast.success('Event deleted'); fetchEvents(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) {
      toast.error('Only PNG, JPG, and WebP images are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be under 5MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload/logo`, fd);
      setFormData(prev => ({ ...prev, image_url: res.data.path }));
      toast.success('Image uploaded');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const getImageDisplayUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('cfe-krea/')) return `${API}/files/${url}`;
    return url;
  };

  const viewRegistrations = async (event) => {
    setViewingRegsFor(event);
    setExpandedReg(null);
    try {
      const response = await axios.get(`${API}/events/${event.id}/registrations`, { headers: getAuthHeader() });
      setRegistrations(response.data);
    } catch { toast.error('Failed to load registrations'); }
  };

  const deleteRegistration = async (eventId, regId, teamName) => {
    if (!window.confirm(`Delete registration for "${teamName}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/events/${eventId}/registrations/${regId}`, { headers: getAuthHeader() });
      toast.success('Registration deleted');
      setRegistrations(prev => prev.filter(r => r.id !== regId));
      fetchEvents();
    } catch { toast.error('Failed to delete registration'); }
  };

  const exportRegistrations = async (event) => {
    try {
      const response = await axios.get(`${API}/events/${event.id}/registrations/export`, {
        headers: getAuthHeader(), responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${event.title.replace(/\s+/g, '_')}_registrations.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch { toast.error('Failed to export'); }
  };

  const handleDateTimeChange = (value) => {
    const dt = new Date(value);
    const dateStr = dt.toISOString().split('T')[0];
    const timeStr = dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    setFormData({ ...formData, event_datetime: dt.toISOString(), date: dateStr, time: timeStr });
  };

  const getLocalDatetimeValue = (isoStr) => {
    if (!isoStr) return '';
    try {
      const dt = new Date(isoStr);
      const offset = dt.getTimezoneOffset();
      const local = new Date(dt.getTime() - offset * 60000);
      return local.toISOString().slice(0, 16);
    } catch { return ''; }
  };

  const openModal = (event = null) => {
    if (event) {
      setEditingEvent(event);
      setFormData({ ...defaultForm, ...event });
    } else {
      setEditingEvent(null);
      setFormData(defaultForm);
    }
    setIsModalOpen(true);
  };

  const formatDateTime = (event) => {
    if (event.event_datetime) {
      return new Date(event.event_datetime).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    }
    if (event.date) return `${event.date} ${event.time || ''}`;
    return 'TBD';
  };

  const isEventPast = (event) => {
    if (event.event_datetime) return new Date(event.event_datetime) < new Date();
    return event.is_past;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-[#231F20] font-['Bricolage_Grotesque']">Manage Events</h1>
        <Button onClick={() => openModal()} className="bg-[#075FA3] hover:bg-[#0077B6]" data-testid="add-event-btn"><Plus className="w-4 h-4 mr-2" /> Add Event</Button>
      </div>
      <Card>
        <Table>
          <TableHeader>
            <TableRow><TableHead>Title</TableHead><TableHead>Type</TableHead><TableHead>Date & Time</TableHead><TableHead>Registrations</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {events.map((event) => (
              <TableRow key={event.id}>
                <TableCell className="font-medium">{event.title}</TableCell>
                <TableCell>{event.event_type}</TableCell>
                <TableCell className="text-sm">{formatDateTime(event)}</TableCell>
                <TableCell>{event.registration_count}</TableCell>
                <TableCell><Badge variant={isEventPast(event) ? 'secondary' : 'default'}>{isEventPast(event) ? 'Past' : 'Upcoming'}</Badge></TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => viewRegistrations(event)} title="View registrations"><Users className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => openModal(event)}><Edit className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(event.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {events.length === 0 && !loading && (
              <TableRow><TableCell colSpan={6} className="text-center text-[#58595B] py-8">No events yet</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingEvent ? 'Edit Event' : 'Add Event'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Title *</Label><Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required data-testid="event-title-input" /></div>
            <div><Label>Description *</Label><Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required data-testid="event-desc-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Event Type *</Label>
                <Select value={formData.event_type} onValueChange={(v) => setFormData({ ...formData, event_type: v })}>
                  <SelectTrigger data-testid="event-type-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{['Hackathon', 'Workshop', 'Pitch Night', 'Speaker Session', 'Networking'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Location *</Label><Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} required data-testid="event-location-input" /></div>
            </div>
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
              <Label className="text-[#075FA3] font-semibold">Event Date & Time</Label>
              <p className="text-xs text-[#58595B] mb-2">Leave blank if date/time is not yet finalized</p>
              <Input
                type="datetime-local"
                value={getLocalDatetimeValue(formData.event_datetime)}
                onChange={(e) => handleDateTimeChange(e.target.value)}
                className="bg-white"
                data-testid="event-datetime-input"
              />
              {formData.event_datetime && (
                <Button type="button" variant="ghost" size="sm" className="mt-2 text-xs text-red-500" onClick={() => setFormData({ ...formData, event_datetime: '', date: '', time: '' })}>
                  Clear date/time
                </Button>
              )}
            </div>
            <div>
              <Label className="font-semibold">Event Image / Banner</Label>
              <div className="mt-2 space-y-3">
                <input type="file" accept="image/png,image/jpeg,image/jpg,image/webp" ref={fileInputRef} onChange={handleImageUpload} className="hidden" data-testid="event-image-upload-input" />
                <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="w-full" data-testid="event-upload-btn">
                  {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-2" /> Upload Image from Device</>}
                </Button>
                <div className="flex items-center gap-2 text-xs text-gray-400"><div className="flex-1 h-px bg-gray-200" /><span>or paste URL</span><div className="flex-1 h-px bg-gray-200" /></div>
                <Input value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} placeholder="https://..." className="text-sm" />
                {formData.image_url && (
                  <div className="relative rounded-xl overflow-hidden border border-gray-200">
                    <img src={getImageDisplayUrl(formData.image_url)} alt="Event banner preview" className="w-full h-40 object-cover" />
                    <button type="button" onClick={() => setFormData({ ...formData, image_url: '' })}
                      className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-full w-7 h-7 flex items-center justify-center">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <p className="text-xs text-gray-400">PNG, JPG, WebP — max 5MB</p>
              </div>
            </div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.is_virtual} onChange={(e) => setFormData({ ...formData, is_virtual: e.target.checked })} /> Virtual Event</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.is_past} onChange={(e) => setFormData({ ...formData, is_past: e.target.checked })} /> Mark as Past</label>
            </div>
            <Button type="submit" className="w-full bg-[#075FA3]" data-testid="event-submit-btn">{editingEvent ? 'Update Event' : 'Publish Event'}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Registrations Dialog */}
      <Dialog open={!!viewingRegsFor} onOpenChange={() => setViewingRegsFor(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-['Bricolage_Grotesque']">
              Registrations — {viewingRegsFor?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-[#58595B]">{registrations.length} team{registrations.length !== 1 ? 's' : ''} registered</p>
            {registrations.length > 0 && (
              <Button size="sm" variant="outline" onClick={() => exportRegistrations(viewingRegsFor)} data-testid="export-regs-btn">
                <Download className="w-4 h-4 mr-2" /> Export .xlsx
              </Button>
            )}
          </div>
          {registrations.length === 0 ? (
            <p className="text-center text-gray-400 py-8">No registrations yet</p>
          ) : (
            <div className="space-y-3">
              {registrations.map((reg) => {
                const primary = reg.members?.find(m => m.is_primary) || reg.members?.[0];
                const isExpanded = expandedReg === reg.id;
                return (
                  <div key={reg.id} className="border rounded-xl overflow-hidden">
                    <button onClick={() => setExpandedReg(isExpanded ? null : reg.id)}
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-50 text-left">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-[#075FA3]/10 flex items-center justify-center">
                          <Users className="w-4 h-4 text-[#075FA3]" />
                        </div>
                        <div>
                          <p className="font-semibold text-[#231F20]">{reg.team_name || 'Unnamed Team'}</p>
                          <p className="text-xs text-gray-500">{primary?.name} · {reg.members?.length || 0} member{(reg.members?.length || 0) !== 1 ? 's' : ''}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-400">{new Date(reg.registered_at).toLocaleDateString()}</span>
                        <Button type="button" size="sm" variant="ghost" className="text-red-500 h-7 w-7 p-0"
                          onClick={(e) => { e.stopPropagation(); deleteRegistration(viewingRegsFor.id, reg.id, reg.team_name); }}
                          title="Delete registration">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="border-t px-4 pb-4 pt-3 bg-gray-50/50 space-y-3">
                        {(reg.members || []).map((m, i) => (
                          <div key={i} className="flex items-start gap-3 p-3 bg-white rounded-lg border">
                            <div className="w-7 h-7 rounded-full bg-[#C83041]/10 flex items-center justify-center text-xs font-bold text-[#C83041] flex-shrink-0">{i + 1}</div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-sm">{m.name}</p>
                                {m.is_primary && <Badge className="bg-[#075FA3] text-white text-[10px] px-1.5 py-0">Primary</Badge>}
                              </div>
                              <p className="text-xs text-gray-500">{m.email}{m.phone ? ` · ${m.phone}` : ''}</p>
                              <p className="text-xs text-gray-400">{m.affiliation}{m.batch_year ? ` · ${m.batch_year}` : ''}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
